import React, { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register required Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

type Props = {
  distances?: number[]; // meters
  grades?: number[]; // percent
  canvasId?: string;
  height?: number;
  width?: any;
};

const defaultDistances = [0, 10, 20, 30, 40, 50, 60];
const defaultGrades = [2.5, 3.0, 1.8, 4.2, 3.5, 2.0, 2.8];

const GradesDemChainage: React.FC<Props> = ({
  distances = defaultDistances,
  grades = defaultGrades,
  canvasId = 'slope-chart-canvas',
  height = 200,
  width = 600,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    // Prepare Chart.js data structure
    const data: ChartData<'line'> = {
      labels: distances.map((d) => `${d} m`),
      datasets: [
        {
          label: 'Slope (Grade) %',
          data: grades,
          borderColor: 'rgb(23, 22, 22, 0.2)',
          // backgroundColor: 'rgba(255, 99, 132, 0.15)',
          fill: true,
          segment: {
            // Callback receives context containing point properties
            borderColor: ctx => {
              // Check the value of the target point (p1)
              const val = ctx.p1.parsed.y; 
              return (val && Math.abs(val) <= 3) ? 'rgb(20, 198, 29)' : ( (val && Math.abs(val) > 5)   ? 'rgb(255, 99, 132)' : 'rgb(219, 226, 31)');
            },
            backgroundColor: ctx => {
              // Check the value of the target point (p1)
              const val = ctx.p1.parsed.y; 
              return (val && Math.abs(val) <= 3) ? 'rgb(20, 198, 29, 0.15)' : ( (val && Math.abs(val) > 5)   ? 'rgb(255, 99, 132, 0.15)' : 'rgb(219, 226, 31, 0.15)');
            },
          },
          tension: 0.3,
          // pointRadius: 3,
        },
      ],
    };

    const options: ChartOptions<'line'> = {
      responsive: false, // we control size via canvas props
      plugins: {
        legend: {
          position: 'bottom',
        },
        title: {
          display: true,
          text: 'Terrain Slope (Grade) Along Transect',
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const val = context.parsed.y;
              return `Slope: ${val}%`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true
            },
            mode: 'x',
          },
          pan: {
            enabled: true,
            mode: 'x'
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Distance Along Transect (m)',
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Slope (%)',
          },
        },
      },
    };

    // Destroy existing chart instance if present
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Create Chart.js instance
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data,
      options,
    });

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [distances, grades]);

  return (
    <div>
      <canvas id={canvasId} ref={canvasRef} width={width} height={height} />
    </div>
  );
};

export default GradesDemChainage;